import React, { useEffect, useCallback, useRef } from 'react';
import queryString from 'query-string';
import { randomBytes, createHash } from 'crypto';
import { useLocation } from 'react-router-dom';
import { auth, FROM_PATH_KEY } from '@/constants';
import { urlSafeEncode, NOTICE_MESSAGE_KEY } from '@/components/pages/Auth/Openid/shared/utils';
import i18n from '@/settings/i18n';
import { Locale } from '@/types/openid';
import { openIdVerificationStore } from '@/store/openIdVerification';
import { persistor, useAppDispatch } from '@/store';

type AuthRedirectParams = {
  response_type: 'code';
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  nonce: string;
  type: 'admin';
  code_challenge: string;
  code_challenge_method: 'S256';
  ui_locales: string;
  error_message_key: string;
  notice_message_key: string;
  prompt: string;
  glopla_id: string | null;
};

type ReturnToResponse = {
  returnTo: string;
};

export const Container: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { returnTo } = queryString.parse(location.search) as ReturnToResponse;
  // sessionStorageから先に取得（useEffect実行前に1回だけ）
  // useRefを使用する理由:
  // - useEffectが複数回実行される場合でも、マウント時の値を保持できる
  // - sessionStorageが空の場合は空文字列となり、notice_message_keyは設定されない
  // - PrivateRouteのリダイレクトでURLのクエリパラムが失われるため、sessionStorage経由で値を受け渡す
  const storedNoticeMessageKey = useRef(sessionStorage.getItem(NOTICE_MESSAGE_KEY) || '');

  const createRandomString = (size: number) => {
    return randomBytes(size).reduce((p, i) => p + (i % 36).toString(36), '');
  };

  const createCodeChallenge = useCallback((): [string, string] => {
    const codeVerifier = createRandomString(128);
    const hash = createHash('sha256');
    hash.update(codeVerifier);

    const codeChallenge = urlSafeEncode(hash.digest('base64'));
    return [codeVerifier, codeChallenge];
  }, []);

  /**
   * nonceの作成日時から1時間後の値を返す
   */
  const returnToExpiresAt = (): Date => {
    const now = new Date().getTime();
    // Dateがmsec換算のため×1000を追加
    return new Date(now + 1000 * 60 * 60);
  };

  const createNonce = useCallback((returnTo: string): string => {
    const nonceJson = {
      salt: createRandomString(32),
      returnTo,
      expiresAt: returnToExpiresAt(),
    };
    return btoa(JSON.stringify(nonceJson));
  }, []);

  const locales: Locale[] = i18n.languages as Locale[];

  useEffect(() => {
    (async () => {
      const state = createRandomString(32);
      const nonce = createNonce(returnTo);
      const [codeVerifier, codeChallenge] = createCodeChallenge();

      dispatch(
        openIdVerificationStore.actions.setOpenIdVerification({
          state,
          nonce,
          codeVerifier,
        }),
      );
      const queryParams = new URLSearchParams(location.search);
      const prompt = queryParams.get('prompt') || '';
      const error_message_key = queryParams.get('error_message_key') || '';
      // クエリパラムがない場合はsessionStorageから取得（refに保存済み）
      const notice_message_key =
        queryParams.get('notice_message_key') ||
        storedNoticeMessageKey.current;
      // 使用後は削除
      sessionStorage.removeItem(NOTICE_MESSAGE_KEY);
      const fromPath = queryParams.get(FROM_PATH_KEY);
      const glopla_id = queryParams.get('glopla_id') || '';

      // 戻り先の指定がない認証フローでは、過去の戻り先を持ち越さないようキーごと削除する
      if (fromPath) {
        sessionStorage.setItem(FROM_PATH_KEY, fromPath);
      } else {
        sessionStorage.removeItem(FROM_PATH_KEY);
      }

      const params: AuthRedirectParams = {
        response_type: 'code',
        client_id: process.env.REACT_APP_CLIENT_ID as string,
        redirect_uri: auth.Url.redirectUri,
        scope: auth.SCOPES.join(' '),
        state,
        nonce,
        type: 'admin',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        ui_locales: locales.join(' '),
        prompt,
        error_message_key,
        notice_message_key,
        glopla_id,
      };

      const authorizationUri = `${auth.Url.authorizationEndpointUri}?${queryString.stringify(
        params,
      )}`;
      // MEMO: state/nonce/codeVerifier の sessionStorage への書き込みが完了してから
      // IdP へ navigation する。flush しないと書き込みが navigation に追い越されて
      // callback 到達時に storedState=undefined → 認証エラー画面 の race に陥る。
      await persistor.flush();
      window.location.assign(authorizationUri);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return <React.Fragment />;
};
